import { useEffect, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import cn from 'classnames';
import { ReactSVG } from 'react-svg';
import {
  ONBOARDING_STEP_EVENT,
  WIZARD_AIRPORT_COMPLETED,
  WIZARD_AIRPORT_REGIONS_ANSWERED,
  WIZARD_AIRPORT_REGIONS_LOADED,
  WIZARD_AIRPORTS_LIST_ANSWERED,
  WIZARD_AIRPORTS_LIST_LOADED,
} from '../../../constants/analyticEvents';
import { pushEvent } from '../../../utilities/analytics';
import { setCurrentStep, setDataV2 } from '../onboardingSlice';
import { selectFilterData } from '../../mainFilter/selectors';
import { getWizardV2State } from '../wizardService';
import { isMobile, isRtl } from '../../../utilities/helpers';
import DepartureStepDesktop from '../../wizardDesktop/steps/DepartureStepDesktop';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import FadeTransition from '../../../components/animations/FadeTransition';
import { getImage } from '../../../utilities/iconResolver';
import GroupList from './departure/GroupList';
import { AirportItem } from './departure/types';
import { t } from '../../../utilities/translator';

const DepartureStep = () => {
  const { navbarBack, departureStep: stepConfig } = getWizardV2State();
  const dispatch = useAppDispatch();
  const { searchEngine } = useAppSelector(selectFilterData);
  const { fromwhere, from_where_groups }: {
    fromwhere: AirportItem[],
    from_where_groups: { [key: number]: string }
  } = searchEngine;
  const [departuresTab, setDeparturesTab] = useState<'group' | 'list'>('group');
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const { formV2: form, step } = useAppSelector(state => state.onboarding);
  const selectedDepartures = form.departure.filter((dep: AirportItem) => dep.active);
  const rtl = isRtl();

  useEffect(() => {
    pushEvent(WIZARD_AIRPORT_REGIONS_LOADED);
  }, [dispatch]);

  useEffect(() => {
    if (form.departure.length === 0) {
      const startFromwhere = searchEngine.fromwhere.map((dep: AirportItem) => ({
        ...dep,
        active: false
      }));
      dispatch(setDataV2({ departure: startFromwhere }));
    }
  }, [dispatch, searchEngine.fromwhere, form.departure])

  const handleCheckbox = (airportCode: string) => {
    const newSelectedDeparture = form.departure.map((dep: AirportItem) => ({
      ...dep,
      active: (airportCode === dep.id) ? !dep.active : dep.active
    }));
    dispatch(setDataV2({ departure: newSelectedDeparture }));
  };

  const handleCheckboxGroup = (group: number, value: boolean) => {
    const newSelectedDeparture = form.departure.map((dep: AirportItem) => ({
      ...dep,
      active: (group === dep.group_id) ? value : dep.active
    }));
    dispatch(setDataV2({ departure: newSelectedDeparture }));
  }

  const handleContinueClick = () => {
    if (!selectedDepartures.length) {
      dispatch(setDataV2({ departure: fromwhere, departureSelectedByUser: false }));
    }
    dispatch(setCurrentStep(1));
    const openedBlocks = Array.prototype.slice.call(document.getElementsByClassName('dlgt-opened')).map(area => area.textContent);
    pushEvent(ONBOARDING_STEP_EVENT, {
      step,
      stepName: 'departure',
      question: stepConfig.title,
      selection: form.departure,
      skipAvailable: !!(localStorage.getItem('onboardingCompleted')),
      openedBlocks,
    });

    const selectedAirports = form.departure.filter((dep: AirportItem) => dep.active)
    const selectedGroups = Array.from(new Set(selectedAirports.map((dep: AirportItem) => from_where_groups[dep.group_id])));
    pushEvent(WIZARD_AIRPORT_COMPLETED, { groups: selectedGroups, airports: selectedAirports.map((dep: AirportItem) => dep.id) });
  };

  const handleSaveAirportSelection = () => {
    setDeparturesTab('group');
    const selectedAirports = form.departure.filter((dep: AirportItem) => dep.active).map((dep: AirportItem) => dep.id);
    pushEvent(WIZARD_AIRPORTS_LIST_ANSWERED, { selection: selectedAirports });
  }

  const showAirportsList = () => {
    setDeparturesTab('list');
    pushEvent(WIZARD_AIRPORTS_LIST_LOADED);
  }

  const onGroupClick = (group: number) => {
    setSelectedGroup(group);
    pushEvent(WIZARD_AIRPORT_REGIONS_ANSWERED, { group: from_where_groups[group] });
    showAirportsList();
  }

  const getGroupList = (group: number) => {
    return form.departure.filter((dep: AirportItem) => dep.group_id === group);
  }

  const showAllAirportsClick = () => {
    setSelectedGroup(null);
    setDeparturesTab('list');
    pushEvent(WIZARD_AIRPORTS_LIST_LOADED);
  }

  if (!isMobile()) {
    return (
      <DepartureStepDesktop
        fromWhereGroups={from_where_groups}
        handleCheckbox={handleCheckbox}
        stepConfig={stepConfig}
        onContinueClick={handleContinueClick}
      />
    )
  }

  return (
    <div>
      <SwitchTransition mode='out-in'>
        <FadeTransition key={departuresTab} classNames='fade'>
          <div>
            {departuresTab === 'group' && (
              <div>
                <div className="wizard-step wizard-departure-step">
                  <div className="flex flex-col gap-3">
                    <div className="wizard-step__title text-lg font-semibold">{t('mainFilter.departureGroupsTitle')}</div>
                    <span className="text-center text-tertiary text-sm">{t('mainFilter.departureGroupsSubtitle')}</span>
                  </div>
                  <div className="slide-in-bottom airports-group-wrap">
                    {[4, 1, 2, 3].map((group) => (
                      <div key={group} className={cn('airports-group', { selected: getGroupList(group).filter((dep: AirportItem) => dep.active).length > 0 })} onClick={() => onGroupClick(group)}>
                        <ReactSVG src={getImage(`departure-group-${group}.svg`)} />
                        {t(`mainFilter.${from_where_groups[group]}`)}
                        {getGroupList(group).filter((dep: AirportItem) => dep.active).length > 0 && <span className="airports-group__counter">{getGroupList(group).filter((dep: AirportItem) => dep.active).length}</span>}
                      </div>
                    ))}
                  </div>
                  <span className="slide-in-bottom text-center text-tertiary text-sm underline" onClick={showAllAirportsClick}>{t('mainFilter.showAllAirports')}</span>
                </div>
                <div className="wizard-footer">
                  <button className="wizard-control-button" onClick={handleContinueClick}>{stepConfig.buttonText}</button>
                </div>
              </div>
            )}
            {departuresTab === 'list' && (
              <div className="wizard-airports-list">
                <div className="wizard-navigation__back !font-semibold" onClick={() => setDeparturesTab('group')}>
                  {rtl ? (
                    <i className="ri-arrow-right-s-line" />
                  ) : (
                    <i className="ri-arrow-left-s-line" />
                  )}
                  <span>{navbarBack}</span>
                </div>
                <div className="wizard-step wizard-departure-step">
                  <div className="text-center flex flex-col gap-3">
                    <div className="wizard-step__title text-lg font-semibold">{selectedGroup ? t('mainFilter.departureListTitle', { group: t(`mainFilter.${from_where_groups[selectedGroup]}`) }) : t('mainFilter.departureGroupsTitle') }</div>
                    <div className="text-tertiary text-sm">{t('mainFilter.multipleOptionsAvailable')}</div>
                  </div>
                  <div className="slide-in-bottom flex flex-col mt-10 gap-10">
                    {selectedGroup && (
                      <>
                        <GroupList
                          airports={getGroupList(selectedGroup)}
                          group={selectedGroup}
                          groups={from_where_groups}
                          onCheckboxClick={handleCheckbox}
                          onGroupCheckboxClick={handleCheckboxGroup}
                          selection={form.departure}
                        />
                        <span className="slide-in-bottom text-center text-tertiary text-sm underline" onClick={showAllAirportsClick}>{t('mainFilter.showAllAirports')}</span>
                      </>
                    )}
                    {!selectedGroup && [4, 1, 2, 3].map((group) => (
                      <div>
                        <div className="mb-5 text-lg font-semibold text-start">{t(`mainFilter.${from_where_groups[group]}`)}</div>
                        <GroupList
                          airports={getGroupList(group)}
                          group={group}
                          groups={from_where_groups}
                          onCheckboxClick={handleCheckbox}
                          onGroupCheckboxClick={handleCheckboxGroup}
                          selection={form.departure}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="wizard-footer">
                  <button className="wizard-control-button" onClick={handleSaveAirportSelection}>{t('buttons.Save')}</button>
                </div>
              </div>
            )}
          </div>
        </FadeTransition>
      </SwitchTransition>
    </div>
  );
}

export default DepartureStep;
